import { StyledDialog } from '@blackhyve/common';
import { parseDate } from '@blackhyve/utilities/dates';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Table } from 'components/table/Table';
import differenceInDays from 'date-fns/differenceInDays';
import { useGetCompaniesQuery } from 'features/companies/api/companies';
import { useState } from 'react';
import StuckPointDetails from './StuckPointDetails';
import StuckPointMoreOptions from './StuckPointMoreOptions';

export const stuckPointTableColumns = [
  {
    label: 'Stuck Point Title',
    field: 'title',
    sort: false,
  },
  {
    label: 'Task Name',
    field: 'task_name',
    sort: false,
    render: ({ task }) => {
      return task?.name;
    },
  },
  {
    label: 'Date Opened',
    field: 'opened_on',
    sort: true,
    comparator: () => (a, b, direction) => {
      return parseDate(a) - parseDate(b);
    },
    render: ({ opened_on }) => {
      return parseDate(opened_on).toLocaleDateString();
    },
  },
  {
    label: 'Date Resolved',
    field: 'resolved_on',
    sort: true,
    comparator: () => (a, b, direction) => {
      return parseDate(a) - parseDate(b);
    },
    render: ({ resolved_on }) => {
      return resolved_on && parseDate(resolved_on).toLocaleDateString();
    },
  },
  {
    label: 'Days Stuck',
    field: 'days_stuck',
    sort: true,
    render: ({ opened_on, is_resolved, resolved_on }) => {
      return is_resolved
        ? differenceInDays(parseDate(resolved_on), parseDate(opened_on))
        : differenceInDays(new Date(), parseDate(opened_on));
    },
  },
  {
    label: 'Affects PR',
    field: 'affects_pr',
    sort: true,
    render: ({ affects_pr }) => (affects_pr ? 'Yes' : 'No'),
  },

  {
    label: 'Stuck By',
    field: 'stuck_by',
    sort: false,
    render: ({ companies_to_blame }) => {
      return <CompaniesToBlame companyIds={companies_to_blame} />;
    },
  },
  {
    label: 'Comments',
    field: 'comments',
    render: ({ comments_count }) => {
      return (
        <Stack direction={'row'} spacing={1}>
          <ForumOutlinedIcon color={comments_count > 0 ? 'info' : ''} />
          <Typography>{comments_count}</Typography>
        </Stack>
      );
    },
  },
  {
    label: 'Status',
    field: 'status',
    sort: true,
    render: ({ is_resolved }) =>
      is_resolved ? (
        <Chip color="success" label="Resolved" variant="outlined" />
      ) : (
        <Chip color="error" label="Pending" variant="outlined" />
      ),
  },

  {
    field: 'moreOptions',
    cellProps: { align: 'right', onClick: (e) => e.stopPropagation() },
    render: (stuckPoint, meta) => <StuckPointMoreOptions stuckPoint={stuckPoint} />,
  },
];

const StuckPointTable = ({ stuckPoints, isFetching, isLoading }) => {
  const [openStuckPointDetails, setOpenStuckPointDetails] = useState(undefined);
  const projectId = openStuckPointDetails
    ? stuckPoints.find((stuckPoint) => stuckPoint.id === openStuckPointDetails).project_id
    : undefined;

  return (
    <>
      <Table
        columns={stuckPointTableColumns}
        defaultOrderBy={false}
        isFetching={isFetching}
        isLoading={isLoading}
        rows={stuckPoints}
        stickyHeader={true}
        rowProps={(row) => ({
          onClick: (e) => {
            console.log(row);
            setOpenStuckPointDetails(row.id);
          },
          sx: { cursor: 'pointer' },
        })}
      />
      <StyledDialog
        handleClose={() => setOpenStuckPointDetails(undefined)}
        maxWidth={'md'}
        open={Boolean(openStuckPointDetails)}
        scroll={'body'}
        title={''}
      >
        <StuckPointDetails id={openStuckPointDetails} projectId={projectId} />
      </StyledDialog>
    </>
  );
};

/**
 * Companies to blame
 * @param {[string]} companyIds
 * @returns
 */
export const CompaniesToBlame = ({ companyIds }) => {
  const { data: companies = [] } = useGetCompaniesQuery();

  const companyDetails = companyIds
    ?.map((companyId) => {
      return companies.find((company) => company.id === companyId);
    })
    .filter((company) => company);

  return companyDetails.map((company, index) => {
    return (
      <Typography key={company.id} variant="subtitle2">
        {company.name}
        {companyDetails.length > index + 1 ? ', ' : ''}
      </Typography>
    );
  });
};

export default StuckPointTable;
